import React, { useState, useRef, useCallback, useEffect } from 'react';
import T from 'prop-types';
import { fromJS } from 'immutable';
import TMC from '@autonomic/browser-sdk';
import AutoIntl from '@au/core/lib/components/elements/AutoIntl';
import Popout from '@au/core/lib/components/objects/Popout';
import DynamicDataTable from '@au/core/lib/components/elements/dynamic-data-table';
import SearchBox from '@au/core/lib/components/elements/SearchBox';
import { formatMessage } from '../../utils/reactIntl';
import { createResponseAlertMessage } from '@au/core/lib/components/objects/AlertMessage';

import styles from '../../css/components/popout/conversion_rules_popout.module.scss';

const tableDef = {
  tablePkField: 'id',
  columnDefs: [
    {
      property: 'id',
      labelId: 'au.entity.attr.id',
      style: { width: 100, flexGrow: 3 },
      display: true,
    },
    {
      property: 'sourceMessageName',
      labelId: 'au.entity.attr.sourceMessageName',
      style: { width: 100, flexGrow: 3 },
      display: true,
    },
    {
      property: 'versionCount',
      labelId: 'au.entity.attr.versionCount',
      style: { width: 100, flexGrow: 1 },
      display: true,
    },
    {
      property: 'lastModifiedTime',
      labelId: 'au.updateTime',
      style: { width: 100, flexGrow: 3 },
      display: true,
      formatters: [{ func: 'date' }],
    },
  ],
};

const getColumnDefs = (columns) =>
  columns.map((column) => ({
    property: column.property,
    label: formatMessage({ id: column.labelId }),
    display: column.display,
    style: column.style,
    formatters: column.formatters,
  }));

const ConversionRulesPopout = ({ sourceEnv, onClose }) => {
  const [searchText, setSearchText] = useState('');
  const [conversionRules, setConversionRules] = useState(fromJS([]));
  const [nextPageToken, setNextPageToken] = useState(null);
  const [fetching, setFetching] = useState(false);

  // we can't rely on state since it's async.
  // use `fetching` state to trigger React re-rendering
  // use fetchingNextPageRef to prevent multiple fetches
  const fetchingNextPageRef = useRef(false);

  const endpointRef = useRef(null);
  if (endpointRef.current === null) {
    endpointRef.current = new TMC.services.TelemetryDefinition().syncConfigs;
  }

  const latestRequestId = useRef(0);

  const fetchData = useCallback(
    (pageToken, search, append, isCancelled = () => false) => {
      const requestId = ++latestRequestId.current;
      const params = { size: 20 };
      if (pageToken) params.pageToken = pageToken;
      if (search) params.messageName = search;

      endpointRef.current
        .listAllRules(sourceEnv, params)
        .then((resp) => {
          if (requestId !== latestRequestId.current || isCancelled()) return;

          const newConversionRules = fromJS(resp.data.items);

          fetchingNextPageRef.current = false;

          setConversionRules((prevConversionRules) =>
            append ? prevConversionRules.concat(newConversionRules) : newConversionRules,
          );
          setNextPageToken(resp.data.nextPageToken);
          setFetching(false);
        })
        .catch((e) => {
          if (isCancelled()) return;
          fetchingNextPageRef.current = false;
          setFetching(false);
          createResponseAlertMessage(e);
        });
    },
    [sourceEnv],
  );

  useEffect(() => {
    let cancelled = false;

    setSearchText('');
    setNextPageToken('');
    fetchData('', '', false, () => cancelled);

    return () => {
      cancelled = true;
    };
  }, [fetchData]);

  const fetchNextPage = useCallback(() => {
    if (!fetchingNextPageRef.current) {
      fetchingNextPageRef.current = true;
      setFetching(true);
      fetchData(nextPageToken, searchText, true);
    }
  }, [fetchData, nextPageToken, searchText]);

  const onSearchChange = useCallback(
    (text) => {
      if (!fetchingNextPageRef.current) {
        fetchingNextPageRef.current = true;
        setSearchText(text);
        setNextPageToken('');
        setFetching(true);
        fetchData('', text, false);
      }
    },
    [fetchData],
  );

  const hasNextPage = nextPageToken !== '';
  const header = <AutoIntl displayId='au.entity.title.conversion' tag={'span'} className={styles.popout_header} />;
  const isLoading = fetching || fetchingNextPageRef.current;

  return (
    <Popout
      className={styles.popout}
      buttonClassName={styles.popout_close}
      popoutHeader={header}
      nuevo
      resizable
      onCloseClick={onClose}
      initialWidth={window.innerWidth / 1.5}
      hideFooter={true}
    >
      <div className={styles.popout_content}>
        <div className={styles.table_control}>
          <SearchBox
            loading={isLoading}
            value={searchText}
            debounce={1000}
            className={styles.searchBox}
            placeholderId='au.telemetry.conversion.search'
            onChange={onSearchChange}
          />
        </div>

        <div className={styles.table_container}>
          <DynamicDataTable
            tablePkField={tableDef.tablePkField}
            columns={getColumnDefs(tableDef.columnDefs)}
            data={conversionRules}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isLoading}
          />
        </div>
      </div>
    </Popout>
  );
};

ConversionRulesPopout.propTypes = {
  sourceEnv: T.string,
  onClose: T.func,
};

export default ConversionRulesPopout;
